import { Fragment } from "react";

import {
  formatTemporalAxisLabel,
  isTemporalCategoryLabel,
} from "@/lib/weekDates";

import type { ChartTheme } from "./theme";
import { formatAxisTick, type CartesianModel } from "./useChartModel";

function formatAxisCategory(category: string, maxLen = 12): string {
  if (isTemporalCategoryLabel(category)) {
    return formatTemporalAxisLabel(category);
  }
  return category.length > maxLen ? `${category.slice(0, maxLen - 1)}…` : category;
}

interface CartesianAxesProps {
  model: CartesianModel;
  theme: ChartTheme;
  xTitle?: string;
  yTitle?: string;
  y2Title?: string;
  /** Blur x-axis category labels (restricted PII). */
  maskCategory?: boolean;
  /** Blur y-axis value ticks (restricted money). */
  maskValue?: boolean;
}

function verticalAxes({
  model,
  theme,
  xTitle,
  yTitle,
  y2Title,
  maskCategory,
  maskValue,
}: CartesianAxesProps) {
  const {
    innerWidth,
    innerHeight,
    x,
    y,
    yTickValues,
    categories,
    labelStep,
    fontSize,
    logY,
    y2,
    y2TickValues,
    yFormat,
    y2Format,
  } = model;
  const zeroY = logY ? Number.NaN : y(0);

  return (
    <g>
      {yTickValues.map((tick) => {
        const ty = y(tick);
        const isZero = tick === 0;
        return (
          <Fragment key={`y-${tick}`}>
            <line
              x1={0}
              x2={innerWidth}
              y1={ty}
              y2={ty}
              stroke={theme.grid}
              strokeWidth={isZero ? 1.25 : 1}
            />
            <text
              x={-8}
              y={ty}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={fontSize}
              fill={theme.axis}
              className={maskValue && !isZero ? "sensitive-blur" : undefined}
            >
              {formatAxisTick(tick, yFormat)}
            </text>
          </Fragment>
        );
      })}

      {y2 &&
        y2TickValues?.map((tick) => {
          const ty = y2(tick);
          return (
            <text
              key={`y2-${tick}`}
              x={innerWidth + 8}
              y={ty}
              textAnchor="start"
              dominantBaseline="middle"
              fontSize={fontSize}
              fill={theme.axis}
              className={maskValue && tick !== 0 ? "sensitive-blur" : undefined}
            >
              {formatAxisTick(tick, y2Format)}
            </text>
          );
        })}

      {categories.map((cat, i) => {
        if (i % labelStep !== 0) return null;
        const bandX = x(cat) ?? 0;
        const cx = bandX + x.bandwidth() / 2;
        return (
          <text
            key={`x-${cat}`}
            x={cx}
            y={innerHeight + fontSize + 6}
            textAnchor="middle"
            fontSize={fontSize}
            fill={theme.axis}
            className={maskCategory ? "sensitive-blur" : undefined}
          >
            {formatAxisCategory(cat)}
          </text>
        );
      })}

      {zeroY > 0 && zeroY < innerHeight && (
        <line
          x1={0}
          x2={innerWidth}
          y1={zeroY}
          y2={zeroY}
          stroke={theme.axis}
          strokeOpacity={0.5}
          strokeWidth={1}
        />
      )}

      {yTitle && (
        <text
          transform={`translate(${-model.margin.left + fontSize},${innerHeight / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={fontSize}
          fill={theme.axis}
        >
          {yTitle}
        </text>
      )}
      {y2Title && y2 && (
        <text
          transform={`translate(${innerWidth + model.margin.right - fontSize},${innerHeight / 2}) rotate(90)`}
          textAnchor="middle"
          fontSize={fontSize}
          fill={theme.axis}
        >
          {y2Title}
        </text>
      )}
      {xTitle && (
        <text
          x={innerWidth / 2}
          y={innerHeight + model.margin.bottom - 2}
          textAnchor="middle"
          fontSize={fontSize}
          fill={theme.axis}
        >
          {xTitle}
        </text>
      )}
    </g>
  );
}

function horizontalAxes({
  model,
  theme,
  xTitle,
  yTitle,
  maskCategory,
  maskValue,
}: CartesianAxesProps) {
  const {
    innerWidth,
    innerHeight,
    categories,
    labelStep,
    fontSize,
    logY,
    yBand,
    xValue,
    xValueBaseline,
    xValueTickValues,
    yFormat,
  } = model;
  if (!yBand || !xValue || xValueBaseline == null || !xValueTickValues) return null;

  const zeroX = logY ? Number.NaN : xValue(0);

  return (
    <g>
      {xValueTickValues.map((tick) => {
        const tx = xValue(tick);
        const isZero = tick === 0;
        return (
          <Fragment key={`x-${tick}`}>
            <line
              x1={tx}
              x2={tx}
              y1={0}
              y2={innerHeight}
              stroke={theme.grid}
              strokeWidth={isZero ? 1.25 : 1}
            />
            <text
              x={tx}
              y={innerHeight + fontSize + 6}
              textAnchor="middle"
              fontSize={fontSize}
              fill={theme.axis}
              className={maskValue && !isZero ? "sensitive-blur" : undefined}
            >
              {formatAxisTick(tick, yFormat)}
            </text>
          </Fragment>
        );
      })}

      {categories.map((cat, i) => {
        if (i % labelStep !== 0) return null;
        const bandY = yBand(cat) ?? 0;
        const bandH = yBand.bandwidth();
        const cy = bandY + bandH / 2;
        return (
          <text
            key={`y-${cat}`}
            x={-8}
            y={cy}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={fontSize}
            fill={theme.axis}
            className={maskCategory ? "sensitive-blur" : undefined}
          >
            {formatAxisCategory(cat, 16)}
          </text>
        );
      })}

      {zeroX > 0 && zeroX < innerWidth && (
        <line
          x1={zeroX}
          x2={zeroX}
          y1={0}
          y2={innerHeight}
          stroke={theme.axis}
          strokeOpacity={0.5}
          strokeWidth={1}
        />
      )}

      {yTitle && (
        <text
          transform={`translate(${-model.margin.left + fontSize},${innerHeight / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={fontSize}
          fill={theme.axis}
        >
          {yTitle}
        </text>
      )}
      {xTitle && (
        <text
          x={innerWidth / 2}
          y={innerHeight + model.margin.bottom - 2}
          textAnchor="middle"
          fontSize={fontSize}
          fill={theme.axis}
        >
          {xTitle}
        </text>
      )}
    </g>
  );
}

/**
 * Gridlines, tick labels, and optional axis titles for a cartesian plot.
 * Render this inside a `<g>` already translated by the chart margin so its
 * origin sits at the top-left of the inner plot area.
 */
export function CartesianAxes(props: CartesianAxesProps) {
  return props.model.horizontal ? horizontalAxes(props) : verticalAxes(props);
}
